/**
 * @file Manages all the data access logic for the 'orders' collection in Firestore.
 */
import { db } from '../firebase';
import { doc, getDoc } from "firebase/firestore";
import { Order } from '../models/types';

/**
 * Helper function to add timeout to promises
 */
const withTimeout = <T>(promise: Promise<T>, timeoutMs: number): Promise<T> => {
    return Promise.race([
        promise,
        new Promise<never>((_, reject) =>
            setTimeout(() => reject(new Error('Request timeout')), timeoutMs)
        )
    ]);
};

/**
 * Retry function for Firebase operations
 */
const retryOperation = async <T>(
    operation: () => Promise<T>,
    maxRetries: number = 3,
    delay: number = 1000
): Promise<T> => {
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
        try {
            return await operation();
        } catch (error: any) {
            console.log(`[Repository] Attempt ${attempt} failed:`, error.message);

            if (attempt === maxRetries) {
                throw error;
            }

            // Wait before retrying
            await new Promise(resolve => setTimeout(resolve, delay * attempt));
        }
    }
    throw new Error('All retry attempts failed');
};

/**
 * Fetches a single order document from Firestore by its ID.
 * @param orderId - The unique identifier of the order to fetch.
 * @returns A promise that resolves to the order data object if found, otherwise null.
 * @throws Throws an error if the fetch operation fails.
 */
export const getOrderById = async (orderId: string): Promise<Order | null> => {
    if (!orderId) {
        throw new Error("Order ID must be provided.");
    }

    try {
        console.log('[Repository] Fetching order:', orderId);

        const result = await retryOperation(async () => {
            const docRef = doc(db, "boxes", orderId);
            return await withTimeout(getDoc(docRef), 10000); // 10 second timeout
        }, 3, 2000); // 3 retries with 2 second delays

        if (result.exists()) {
            console.log('[Repository] Order found successfully:', orderId);
            return result.data() as Order;
        } else {
            console.log('[Repository] Order not found:', orderId);
            return null;
        }
    } catch (error: any) {
        console.error('[Repository] Error fetching order:', error);

        // Handle timeout errors
        if (error.message === 'Request timeout') {
            throw new Error("La conexión está tardando demasiado. Verifica tu conexión a internet.");
        }

        // Handle offline errors specifically
        if (error.code === 'unavailable' || error.message?.includes('offline')) {
            throw new Error("No se puede conectar al servidor. Verifica tu conexión a internet y vuelve a intentar.");
        }

        // Handle permission errors
        if (error.code === 'permission-denied') {
            throw new Error("No tienes permisos para acceder a este pedido.");
        }

        // Handle other Firebase errors
        if (error.code) {
            throw new Error(`Error del servidor: ${error.message}`);
        }

        // Handle generic errors
        throw new Error("Error inesperado al cargar el pedido. Intenta nuevamente.");
    }
};
